import {useEffect, useRef, useState} from 'react';
import {QuizStatus, QuizProperties, UseQuizReturn} from '../quiz.types';

/**
 * Custom hook to manage OutGrow quiz integration
 *
 * ARCHITECTURE:
 * 1. Load OutGrow external script (first useEffect)
 * 2. Initialize iframe when script is ready (second useEffect)
 * 3. Handle states: idle → loading → ready (or error at any point)
 * 4. Clean up resources to prevent memory leaks
 *
 * COMPLEXITY JUSTIFIED:
 * - OutGrow is a third-party service that injects iframes asynchronously
 * - We need to detect when the iframe appears in the DOM (MutationObserver)
 * - Robust error handling and timeouts for reliable UX
 * - SPA optimizations (avoid duplicate scripts, proper cleanup)
 */
export function useQuiz(quizProperties: QuizProperties): UseQuizReturn {
    const [status, setStatus] = useState<QuizStatus>('idle');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const timeoutRef = useRef<number | null>(null);
    const observerRef = useRef<MutationObserver | null>(null);

    const cleanupTimers = () => {
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const markError = (reason?: unknown) => {
        if (process.env.NODE_ENV !== 'production') {
            console.error('[Quiz] Error:', reason);
        }
        cleanupTimers();
        setStatus('error');
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;

        let cancelled = false;

        const onScriptLoaded = () => {
            if (!cancelled) setStatus('loading');
        };

        const onScriptError = () => {
            if (!cancelled) markError(new Error('Error loading quiz script'));
        };

        if (window.initIframe) {
            onScriptLoaded();
            return () => {};
        }

        let script = document.querySelector<HTMLScriptElement>(
            `script[src="${quizProperties.loaderSrc}"]`,
        );

        if (!script) {
            script = document.createElement('script');
            script.src = quizProperties.loaderSrc;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }

        script.addEventListener('load', onScriptLoaded, {once: true});
        script.addEventListener('error', onScriptError, {once: true});

        return () => {
            cancelled = true;
            script?.removeEventListener('load', onScriptLoaded);
            script?.removeEventListener('error', onScriptError);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizProperties.loaderSrc]);

    useEffect(() => {
        if (status !== 'loading') return;

        if (!containerRef.current) return;

        try {
            if (!window.initIframe) {
                throw new Error('window.initIframe not available after loading script.');
            }

            window.initIframe(quizProperties.id);

            const attachIframeListeners = (iframe: HTMLIFrameElement) => {
                const onLoad = () => {
                    cleanupTimers();
                    setStatus('ready');
                };

                const onError = () => {
                    markError(new Error('Error loading quiz iframe'));
                };

                iframe.addEventListener('load', onLoad, {once: true});
                iframe.addEventListener('error', onError, {once: true});

                // Safety timeout: if iframe doesn't load in 8s, consider it an error
                // 8 seconds is a balance between UX and slow networks, this can be changed
                timeoutRef.current = window.setTimeout(() => {
                    markError(new Error('Timeout: quiz iframe did not load'));
                }, 8000);
            };

            const findIframe = () =>
                containerRef.current?.querySelector('iframe') as HTMLIFrameElement | null;

            const existing = findIframe();
            if (existing) {
                attachIframeListeners(existing);
            } else {
                observerRef.current = new MutationObserver(() => {
                    const iframe = findIframe();
                    if (iframe) {
                        observerRef.current?.disconnect();
                        attachIframeListeners(iframe);
                    }
                });

                observerRef.current.observe(containerRef.current, {
                    childList: true,
                    subtree: true,
                });
            }
        } catch (e) {
            markError(e);
        }

        return () => {
            cleanupTimers();
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, quizProperties.id]);

    const hiddenUntilReady = status !== 'ready';

    return {
        status,
        containerRef,
        hiddenUntilReady,
    };
}
